/**
 * All possible steps available when progressing through a ConfigMigration import.
 * This unified enum contains steps for both legacy UI and tabs UI.
 */
export enum MigrationStep {
  UPLOAD = 'UPLOAD',
  // Legacy-specific middle steps (also serve as planning categories)
  TENANT_SETTINGS = 'TENANT_SETTINGS',
  SCHEMAS = 'SCHEMAS',
  TEMPLATES = 'TEMPLATES',
  PROCESS_TASK_SCHEMAS = 'PROCESS_TASK_SCHEMAS',
  STUDIES = 'STUDIES',
  // Tabs-specific step (contains all planning categories as tabs)
  PLAN = 'PLAN',
  // Shared final step
  REVIEW = 'REVIEW',
}

export const INITIAL_STEP = MigrationStep.UPLOAD;

/**
 * The 7-step wizard flow for the legacy UI.
 * Excludes the PLAN step which is tabs-UI specific.
 */
export type LegacyWizardStep = Exclude<MigrationStep, MigrationStep.PLAN>;

/**
 * The 3-step wizard flow for the tabs UI.
 * Planning happens within tabs in the PLAN step.
 */
export type TabsWizardStep = MigrationStep.UPLOAD | MigrationStep.PLAN | MigrationStep.REVIEW;

/**
 * The 5 planning categories shared between legacy UI (as steps) and tabs UI (as tabs).
 * These represent the different content types that can be planned during import.
 */
export type PlanningCategory =
  | MigrationStep.TENANT_SETTINGS
  | MigrationStep.SCHEMAS
  | MigrationStep.TEMPLATES
  | MigrationStep.PROCESS_TASK_SCHEMAS
  | MigrationStep.STUDIES;

/** Array of all planning categories in display order */
export const ALL_PLANNING_CATEGORIES: Array<PlanningCategory> = [
  MigrationStep.TENANT_SETTINGS,
  MigrationStep.SCHEMAS,
  MigrationStep.TEMPLATES,
  MigrationStep.PROCESS_TASK_SCHEMAS,
  MigrationStep.STUDIES,
];

/** All Migration steps that can be planned. Alias for PlanningCategory for backwards compatibility. */
export type PlannableMigrationStep = PlanningCategory;

/** Tabs UI alias - the tabs within the PLAN step. Alias for PlanningCategory for backwards compatibility. */
export type PlanningTab = PlanningCategory;

/** The string to display when there are no items to be planned on the current category. */
export const PLANNING_CATEGORY_TO_EMPTY_DISPLAY_TEXT: {[key in PlanningCategory]: string} = {
  [MigrationStep.SCHEMAS]: 'No schema changes included in this migration.',
  [MigrationStep.TENANT_SETTINGS]: 'No tenant settings changes included in this migration.',
  [MigrationStep.TEMPLATES]: 'No template changes included in this migration.',
  [MigrationStep.PROCESS_TASK_SCHEMAS]: 'No workflow item changes included in this migration.',
  [MigrationStep.STUDIES]: 'No study schema changes included in this migration.',
};

// Legacy aliases for backwards compatibility
export const PLANNABLE_STEPS_TO_EMPTY_DISPLAY_TEXT = PLANNING_CATEGORY_TO_EMPTY_DISPLAY_TEXT;
export const PLANNING_TABS_TO_EMPTY_DISPLAY_TEXT = PLANNING_CATEGORY_TO_EMPTY_DISPLAY_TEXT;
